/**
 * Post-Round-96 Execution Loop — Ask PayGBot helper button.
 *
 * Rendered on task surfaces (Dashboard's DailyMissionCard, Weekly
 * Planner rows, Run My Business steps) whenever the resolved
 * `execution_kind` is `human_action` — i.e. commenting, replying,
 * networking, phone calls, or any other task that does not
 * produce an asset PayGBot can generate.
 *
 * Instead of leaving the customer wondering "what do I do?", the
 * button opens Ask PayGBot with the task's intent pre-filled as
 * the opening prompt so PayGBot can help immediately.
 *
 * When the caller has NO task or the task is asset-generating,
 * this component renders nothing.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatCircle } from '@phosphor-icons/react';
import useWorkflowTarget from '@/hooks/useWorkflowTarget';

const AskPayGBotHelperButton = ({
  task,
  className = '',
  testId = 'ask-paygbot-helper-btn',
}) => {
  const navigate = useNavigate();
  const { target } = useWorkflowTarget(task);
  if (!target || target.execution_kind !== 'human_action') return null;

  const prompt = target.intent || target.cta_label || '';

  const handleClick = () => {
    const qs = prompt
      ? `?prompt=${encodeURIComponent(prompt)}`
      : '';
    navigate(`/ask-paygbot${qs}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-full '
        + 'bg-slate-100 border border-slate-200 text-sm font-medium '
        + 'text-slate-700 hover:bg-slate-50 hover:border-slate-300 '
        + 'transition-colors '
        + className
      }
      data-testid={testId}
      data-workflow-task-id={target.task_id || ''}
    >
      <ChatCircle weight="bold" className="w-4 h-4" />
      <span>Need help? Ask PayGBot</span>
    </button>
  );
};

export default AskPayGBotHelperButton;
